import { Component, OnDestroy, OnInit, Output, EventEmitter, DOCUMENT } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AuthService } from 'src/app/login/auth.service';
import { UsersService } from 'src/app/services/users.service';
import { ResponseObjectAPI } from '../../models/responseObjectAPI';
import { HeaderNavService } from '@layout/header-nav/header-nav.service';
import { Header } from './Header';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DomainConfigComponent } from '@app/domain-config.component';
import { DomSanitizer } from '@angular/platform-browser';
import { Module } from '@models/module';
import { ConfirmActionModalComponent } from '@app/trackvision/shared/components/confirm-action-modal/confirm-action-modal.component';
import { LoadingService } from '@services/loading.service';
import { ReportService } from '@services/services/report.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { NotificationsService } from '@services/notifications.service';
import { LinkOAuthDialogComponent } from '@app/dialogs/link-oauth-dialog/link-oauth-dialog.component';
import { brand } from '@app/branding/brand';
import { LexgosignService } from '@services/lexgosign.service';
import { applyLexgosignMenuGate } from '@app/util/navbar-menu.util';
import { applyClmSignModuleVisibility } from '@app/util/clm-sign.util';
import { filterModulesByAnyPermission } from '@app/util/module-visible.util';
import { setForcePasswordChangeFlag } from 'src/app/util/auth/helpers';

@Component({
    selector: 'app-header-nav',
    templateUrl: './header-nav.component.html',
    styleUrls: ['./header-nav.component.scss'],
    standalone: false
})
export class HeaderNavComponent implements OnInit, OnDestroy {
  dataUser;
  @Output() dataSession = new EventEmitter();
  header: Header;
  userManual = {url: null};
  moduleVisible: Module[] = [];
  navbarMenu:any[];
  notificationsEnabled: boolean = false;
  /** Compile-time: InProvider o Thomson Reuters según build. */
  readonly brand = brand;
  readonly academiaUrl = brand.academiaUrl;
  private readonly destroy$ = new Subject<void>();
  constructor(
    public usersService: UsersService,
    public authService: AuthService,
    private headerNav: HeaderNavService,
    private modalService: NgbModal, 
    private domainConfigComponent: DomainConfigComponent,
    private sanitizer: DomSanitizer,
    private _loading: LoadingService,
    private _report_services: ReportService,
    private permissionsService: NgxPermissionsService,
    private notificationsService: NotificationsService,
    private lexgosignService: LexgosignService,
    private router: Router
    ) {

    this.usersService.getDataUser().subscribe((data: ResponseObjectAPI) => {
      this.dataUser = data.result;
      this.dataSession.emit(this.dataUser);
      if (this.dataUser?.forcePasswordChange) {
        setForcePasswordChangeFlag(true);
        this.router.navigate(['/change-password']);
      }
    });

    this.moduleVisible = this.domainConfigComponent.getModuleVisible();
		this.userManual = this.domainConfigComponent.getConfigFlag(['usermanual']);
  }

  async ngOnInit() {
    this.headerNav.customOption.pipe(takeUntil(this.destroy$)).subscribe(msg => {
      this.header = msg;
    });
    this.navbarMenu = applyLexgosignMenuGate(
      this.domainConfigComponent.getConfigNavbarMenu()
    );
    // Primero permisos (sync, con .filter seguro), después gate lexgosign.enabled.
    this.moduleVisible = filterModulesByAnyPermission(
      this.moduleVisible,
      (permission) => this.permissionsService.getPermission(permission)
    );
    // isEnabled ya hace catchError(() => of(false)); no hace falta rama error.
    // El menú admin ya está limpio (applyLexgosignMenuGate no depende de enabled).
    this.lexgosignService.isEnabled().pipe(takeUntil(this.destroy$)).subscribe((enabled) => {
      this.moduleVisible = applyClmSignModuleVisibility(this.moduleVisible, !!enabled);
    });
   
    // Cargar el estado inicial de las notificaciones
    try {
      const status = await this.notificationsService.getNotificationsStatus();
      if (status) {
        this.notificationsEnabled = status.enabled;
      }
    } catch (error) {
      console.error('Error al cargar el estado de notificaciones:', error);
      // Mantener el valor por defecto (false) si hay error
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  modalOpenUserManage(content) {
    this.modalService.open(content, { centered: true, size: 'lg' });
  }

  openLinkOAuth() {
    this.modalService.open(LinkOAuthDialogComponent, { centered: true, size: 'md' });
  }

  /** true si el tenant tiene habilitado el ingreso por imagen (flag de domain config). */
  get imageIntakeEnabled(): boolean {
    return this.domainConfigComponent.isImageIntakeEnabled();
  }

  /**
   * Acceso GLOBAL para crear un requerimiento desde una foto/PDF de un parte/fiscalización.
   * Es transversal a propósito: el flujo destino lo decide la IA (routing), así que no vive
   * dentro del listado de un flujo concreto. Alternativa a WhatsApp mientras no está el canal.
   *
   * El componente se importa de forma DIFERIDA (no top-level) para no arrastrar código de Flows
   * al bundle eager del header global: los tenants sin el feature no lo descargan.
   */
  async openImageIntakeModal() {
    let RequirementImageIntakeComponent;
    try {
      ({ RequirementImageIntakeComponent } = await import(
        '@flows/requirement/requirement-image-intake/requirement-image-intake.component'
      ));
    } catch {
      // ChunkLoadError típico: el bundle cambió de hash tras un deploy y la pestaña abierta tiene
      // el manifiesto viejo. Recargar la app trae el nuevo chunk.
      alert('No pudimos cargar la herramienta. Recarga la página e inténtalo de nuevo.');
      return;
    }
    const ref = this.modalService.open(RequirementImageIntakeComponent, {
      size: 'lg', backdrop: 'static', keyboard: false, centered: true,
    });
    // Si se creó un requerimiento, refrescamos la vista actual para que cualquier listado
    // abierto lo muestre (el header es global; recargar la ruta activa es lo más seguro).
    ref.result.then((outcome) => {
      if (outcome === 'refresh') {
        const url = this.router.url;
        this.router.navigateByUrl('/', { skipLocationChange: true })
          .then(() => this.router.navigateByUrl(url));
      }
    }).catch(() => { /* modal cerrado sin crear: no-op */ });
  }

  modalOpenReport() {
    const confirmModal = this.modalService.open(ConfirmActionModalComponent, { size: 'sm', centered: true });
		confirmModal.componentInstance.message = `¿Estás seguro que desea generar este reporte?`;
		confirmModal.result.then((confirmed) => {
			if (confirmed) {
				this._loading.reloadStart(null, this._loading.blockUIList);
				this._report_services.generateReport().subscribe((resp) => {
        	if (resp["result"]) {
              window.open(resp["result"], '_blank');
					} else {
						alert("error al generar  reporte");
					}
					this._loading.reloadStop(this._loading.blockUIList);
				},
					error => {
						this._loading.reloadStop(this._loading.blockUIList);
					});
			}
		}).catch();

		return;
  }

  onNavigate(){
    window.open("https://www.pjud.cl", "_blank");
  }

  async toggleNotifications(event: any) {
    const enabled = event.target.checked;
    try {
      this._loading.reloadStart(null, this._loading.blockUIList);
      const response = await this.notificationsService.updateNotificationsConfig(enabled, true);
      if (response && response.success !== false) {
        this.notificationsEnabled = enabled;
      } else {
        // Revertir el switch si falla
        event.target.checked = !enabled;
        alert("Error al actualizar la configuración de notificaciones");
      }
      this._loading.reloadStop(this._loading.blockUIList);
    } catch (error) {
      // Revertir el switch si hay error
      event.target.checked = !enabled;
      this._loading.reloadStop(this._loading.blockUIList);
      alert("Error al actualizar la configuración de notificaciones");
    }
  }

}
